import { useState, useEffect } from 'react';

/**
 * Subscribe to a CSS media query from inside a React component.
 *
 * Returns a boolean that is `true` while the query matches, and re-renders
 * the component whenever that match state changes (e.g. window resize,
 * device rotation). Use this when *behaviour* — not just styling — needs to
 * differ by screen size or input type (styling should stay in CSS @media rules).
 *
 * @param {string} query - a media query string, e.g. '(max-width: 600px)'
 * @returns {boolean} whether the query currently matches
 */
export const useMediaQuery = (query) => {
    // Read the initial value synchronously so the first render is already correct.
    const getMatch = () =>
        typeof window !== 'undefined' && window.matchMedia
            ? window.matchMedia(query).matches
            : false;

    const [matches, setMatches] = useState(getMatch);

    useEffect(() => {
        const mediaQueryList = window.matchMedia(query);
        const handleChange = (event) => setMatches(event.matches);

        // Re-sync in case the value changed between the initial render and this effect.
        setMatches(mediaQueryList.matches);
        mediaQueryList.addEventListener('change', handleChange);

        return () => mediaQueryList.removeEventListener('change', handleChange);
    }, [query]);

    return matches;
};

export const useIsMobile = () => useMediaQuery('(max-width: 600px)');

/* True on touch-first devices with no precise pointer / hover (phones, most tablets). */
export const useIsTouch = () => useMediaQuery('(pointer: coarse)');
